import { useState } from 'react';
import { useOrganization, useOrganizationList, useUser } from '@clerk/clerk-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export function OrganizationSwitcher() {
  const { organization } = useOrganization();
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });
  const { user } = useUser();
  const [isChanging, setIsChanging] = useState(false);

  if (!isLoaded || !user) {
    return null;
  }

  // If user has no organizations, don't show the switcher
  if (!userMemberships?.data || userMemberships.data.length === 0) {
    return null;
  }

  // If user only has one organization, show it without dropdown
  if (userMemberships.data.length === 1) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <Building2 className="h-4 w-4" />
        <span className="truncate max-w-[150px]">
          {organization?.name || userMemberships.data[0].organization.name}
        </span>
      </div>
    );
  }

  const handleOrgSwitch = async (orgId: string) => {
    if (orgId === organization?.id || isChanging) return;
    
    setIsChanging(true);
    try {
      await setActive({ organization: orgId });
      // Force a page reload to refresh all data with new org context
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch organization:', error);
      setIsChanging(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-2 max-w-[200px]"
          disabled={isChanging}
        >
          <Building2 className="h-4 w-4 shrink-0" />
          <span className="truncate">
            {organization?.name || 'Select Organization'}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {userMemberships.data.map((membership) => (
          <DropdownMenuItem
            key={membership.organization.id}
            onClick={() => handleOrgSwitch(membership.organization.id)}
            className={cn(
              'flex items-center justify-between cursor-pointer',
              organization?.id === membership.organization.id && 'bg-accent'
            )}
          >
            <span className="truncate">{membership.organization.name}</span>
            {organization?.id === membership.organization.id && (
              <Check className="h-4 w-4 shrink-0" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
